import { Button, Image, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { KYDAdminFeedItem } from "@common/interfaces/KYDOrganization";
import { FaCloudUploadAlt } from "react-icons/fa";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";

import placeholder from "../../placeholder.png";

type CreateUploadUrl = (
  file: File,
  callback: (params: S3Response) => void | Promise<void | string>
) => Promise<unknown>;

const ImageUploadInputWithPreview = ({
  dirtyText,
  feedItem,
  isDisabled = false,
  onFileUploaded,
  createUploadUrl,
  updateLoading,
}: {
  dirtyText: Record<string, any>;
  feedItem?: KYDAdminFeedItem;
  isDisabled?: boolean;
  onFileUploaded: (
    result: S3Response & { fileKey: string; cdn_url: string }
  ) => void;
  createUploadUrl: CreateUploadUrl;
  updateLoading: (id: string, state: boolean) => void;
}) => {
  const [image, setImage] = useState<string | null>(placeholder);
  const uploadRef = useRef<HTMLInputElement>(null);
  const feedItemImageOverride = feedItem?.image;

  const openFileUpload = () => {
    if (uploadRef && uploadRef.current) {
      uploadRef.current.click();
    }
  };

  const imageToDisplay = useMemo(() => {
    if (dirtyText["item.image_override_preview"]) {
      return dirtyText["item.image_override_preview"];
    }

    if (dirtyText["item.image_override"]) {
      return dirtyText["item.image_override"];
    }
    if (dirtyText[`item.image`]) {
      return dirtyText[`item.image`];
    }

    if (feedItemImageOverride) {
      return feedItemImageOverride;
    }

    return image;
  }, [dirtyText, feedItemImageOverride, image]);

  return (
    <VStack width={"full"} alignItems={"center"}>
      <Image
        rounded={"md"}
        maxH="200px"
        minH="200px"
        cursor="pointer"
        onClick={openFileUpload}
        src={imageToDisplay}
      />
      <ReactS3Uploader
        style={{ display: "none" }}
        //@ts-ignore
        inputRef={uploadRef}
        getSignedUrl={(file, callback) => createUploadUrl(file, callback)}
        accept="image/png,image/jpg,image/jpeg"
        onProgress={() => {}}
        onError={() => {
          updateLoading("link", false);
          // keep silent here; upstream handles dialog
        }}
        preprocess={(file, next) => {
          updateLoading("link", true);
          setImage(URL.createObjectURL(file));
          next(file);
        }}
        onFinish={(
          result: S3Response & { fileKey: string; cdn_url: string }
        ) => {
          onFileUploaded(result);
          updateLoading("link", false);
          if (uploadRef.current) {
            (uploadRef.current as HTMLInputElement).value = "";
          }
        }}
        uploadRequestHeaders={(file) => ({
          "Content-Type": file.type,
          "x-amz-acl": "private",
        })}
        contentDisposition="auto"
      />
      <Button
        onClick={openFileUpload}
        aria-label="Upload image"
        variant={"outline"}
        isDisabled={isDisabled}
        leftIcon={<FaCloudUploadAlt />}
      >
        Upload image
      </Button>
    </VStack>
  );
};

export default ImageUploadInputWithPreview;
